/**
 * Per-element Light|Dark color override — a DIFFERENT concept from the
 * builder canvas / Theme Colors kit toggle (`BuilderCanvasModeContext`,
 * `useBuilderCanvasMode`). That toggle previews which Branding kit resolves
 * `var(--tt-color-*)`; this module lets a single element property (e.g.
 * Classic Header background) optionally carry two different values — one
 * for when the *site* (visitor-facing, not the admin dashboard chrome) is in
 * light mode, one for dark — while staying a single stored string so no
 * schema migration is needed.
 *
 * Storage contract: a plain single value (hex / rgba(...) / gradient /
 * `var(--tt-color-*)`) is used in both site modes, exactly like today. A
 * dual value is JSON with an unambiguous prefix that is never valid CSS on
 * its own, so `isDualColorValue` can never false-positive on a legitimate
 * color string:
 *
 *   __tt_ld__:{"light":"<css-color-or-var>","dark":"<css-color-or-var>"}
 *
 * This is NOT the native CSS `light-dark()` function and NOT the `light-dark(...)`
 * string format explored (and reverted) in an earlier pass for the
 * ColorPicker's Custom tab — that was a different, rejected UX (a dual
 * toggle living inside the picker popover). This module backs a field-level
 * control that sits next to the field label, outside the picker entirely.
 * Each side is still just a plain CSS color/var string, validated the same
 * way as today by the shared ColorPicker's own `normalizeColorInput` gate —
 * this module only ever sees already-validated side values.
 */

const DUAL_COLOR_VALUE_PREFIX = "__tt_ld__:";

export type ColorSiteMode = "light" | "dark";

export type DualColorValue = {
	light: string;
	dark: string;
};

/** True when `value` is a serialized dual color value (see module doc comment). Does not validate that each side is a legal CSS color — see `parseDualColorValue`. */
export function isDualColorValue(value: string): boolean {
	return value.trim().startsWith(DUAL_COLOR_VALUE_PREFIX);
}

/**
 * Parses a serialized dual color value. Returns `null` for a plain single
 * value (no prefix), malformed JSON, or JSON where `light`/`dark` aren't both
 * strings — callers should treat `null` as "this is a legacy/default single
 * value," not as an error to surface. A blank side (`""`) is a valid,
 * meaningful result — "no override for this mode" — not a parse failure:
 * `ClassicHeader` (and any other consumer) skips applying an empty side and
 * lets its normal CSS fallback take over, exactly like a blank single value
 * does today.
 */
export function parseDualColorValue(value: string): DualColorValue | null {
	const trimmed = value.trim();
	if (!trimmed.startsWith(DUAL_COLOR_VALUE_PREFIX)) {
		return null;
	}
	const json = trimmed.slice(DUAL_COLOR_VALUE_PREFIX.length);
	let parsed: unknown;
	try {
		parsed = JSON.parse(json);
	} catch {
		return null;
	}
	if (typeof parsed !== "object" || parsed === null) {
		return null;
	}
	const light = (parsed as Record<string, unknown>).light;
	const dark = (parsed as Record<string, unknown>).dark;
	if (typeof light !== "string" || typeof dark !== "string") {
		return null;
	}
	return { light, dark };
}

/** Formats a `DualColorValue` back into the serialized storage string. */
export function stringifyDualColorValue(sides: DualColorValue): string {
	return `${DUAL_COLOR_VALUE_PREFIX}${JSON.stringify(sides)}`;
}

function readSide(stored: string, side: ColorSiteMode): string {
	const dual = parseDualColorValue(stored);
	if (dual) {
		return side === "light" ? dual.light : dual.dark;
	}
	return stored;
}

/**
 * Resolves the CSS to apply at runtime for a known site mode — a plain
 * single value applies to both modes unchanged; a dual value picks its
 * matching side. Used by element runtime code (e.g. `ClassicHeader`) when
 * the active mode is already known at the call site.
 */
export function resolveColorForMode(
	stored: string,
	mode: ColorSiteMode
): string {
	return readSide(stored, mode);
}

/**
 * Inline / stylesheet value for a stored color. Dual `__tt_ld__` blobs are not
 * valid CSS — use the light side for the base rule (dark side is emitted
 * separately under `.dark` / canvas dark mode).
 */
export function cssColorForStylesheet(stored: string): string {
	const dual = parseDualColorValue(stored);
	if (!dual) {
		return stored;
	}
	return dual.light.trim() || dual.dark.trim();
}

/** Dark-mode side of a dual color, or `undefined` when the value is single-mode. */
export function cssColorDarkForStylesheet(stored: string): string | undefined {
	const dual = parseDualColorValue(stored);
	if (!dual) {
		return undefined;
	}
	const dark = dual.dark.trim();
	return dark.length > 0 ? dark : undefined;
}

/**
 * Same resolution as `resolveColorForMode`, named for the inspector-editing
 * call site: "what should the ColorPicker show/edit for this side right now."
 */
export function getEditableSide(stored: string, side: ColorSiteMode): string {
	return readSide(stored, side);
}

/**
 * Commits an edit to one side. A plain single value is promoted to dual the
 * first time either side is written through this function — both sides
 * start equal to the existing single value (then the edited side is
 * overwritten), so the result is immediately predictable WYSIWYG rather
 * than a surprising blank "other" side. If the existing value was itself
 * blank (unset), the untouched side mirrors `next` instead of staying blank,
 * for the same reason — though a blank side is otherwise a perfectly valid
 * dual value (see `parseDualColorValue`). Once dual, each side updates
 * independently.
 */
export function setEditableSide(
	stored: string,
	side: ColorSiteMode,
	next: string
): string {
	const dual = parseDualColorValue(stored);
	if (dual) {
		return side === "light"
			? stringifyDualColorValue({ light: next, dark: dual.dark })
			: stringifyDualColorValue({ light: dual.light, dark: next });
	}
	const base = stored.trim() ? stored : next;
	return side === "light"
		? stringifyDualColorValue({ light: next, dark: base })
		: stringifyDualColorValue({ light: base, dark: next });
}
